import { useCallback, useMemo, useState } from "react";
import { Badge, Flex, Text, TextField } from "@radix-ui/themes";
import { useQueries } from "react-query";
import { Search } from "lucide-react";

import { Loader } from "@/components/ui/loader";
import { useReleaseConversationApi } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import { RegressionSetItem } from "@/v2/entities/regression/Regression.model";
import { filterActionMessages } from "./utils";
import styles from "./styles.module.scss";

interface SetItemsViewProps {
  items: RegressionSetItem[] | undefined;
  isLoading: boolean;
  selectedItemId: string | null;
  onSelectItem: (itemId: string) => void;
}

function useItemTitles(items: RegressionSetItem[] | undefined) {
  const releaseConversationApi = useReleaseConversationApi();

  const sessionItems = useMemo(
    () =>
      (items ?? []).filter(
        (item) => !item.question && !!item.releaseConversationId
      ),
    [items]
  );

  const queries = useQueries(
    sessionItems.map((item) => ({
      queryKey: ["release-conversation-comments", item.releaseConversationId],
      queryFn: () =>
        releaseConversationApi.getReleaseConversationComments(
          item.releaseConversationId!
        ),
      enabled: true,
    }))
  );

  const isLoadingTitles = queries.some((q) => q.isLoading);

  const titleMap = useMemo(() => {
    const map = new Map<string, string>();
    sessionItems.forEach((item, index) => {
      const comments = queries[index]?.data;
      if (comments) {
        const cleaned = filterActionMessages(comments);
        const firstUserComment = cleaned.find((c) => !c.isAgent);
        map.set(item.id, firstUserComment?.comment ?? "Training session");
      }
    });
    return map;
  }, [sessionItems, queries]);

  const getTitle = useCallback(
    (item: RegressionSetItem) => {
      if (item.question) return item.question;
      return titleMap.get(item.id) ?? "Training session";
    },
    [titleMap]
  );

  return { isLoadingTitles, getTitle };
}

export function SetItemsView({
  items,
  isLoading,
  selectedItemId,
  onSelectItem,
}: SetItemsViewProps) {
  const { isLoadingTitles, getTitle } = useItemTitles(items);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const filteredItems = useMemo(() => {
    if (!items) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const title = getTitle(item).toLowerCase();
      const answer = item.expectedAnswer?.toLowerCase() ?? "";
      return title.includes(query) || answer.includes(query);
    });
  }, [items, searchQuery, getTitle]);

  const showLoader = isLoading || isLoadingTitles;

  return (
    <div className={styles.panelContent}>
      {showLoader ? (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      ) : items && items.length > 0 ? (
        <>
          <div className={styles.searchContainer}>
            <TextField.Root
              placeholder="Search items..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="2"
            >
              <TextField.Slot>
                <Search size={14} />
              </TextField.Slot>
            </TextField.Root>
          </div>
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div
                key={item.id}
                className={`${styles.listItem} ${
                  selectedItemId === item.id ? styles.selectedListItem : ""
                }`}
                onClick={() => onSelectItem(item.id)}
              >
                <Flex justify="between" align="start" gap="2">
                  <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                    <Text
                      size="2"
                      weight="medium"
                      className={styles.questionText}
                    >
                      {getTitle(item)}
                    </Text>
                    {item.expectedAnswer && (
                      <Text
                        size="1"
                        color="gray"
                        className={styles.questionText}
                      >
                        {item.expectedAnswer}
                      </Text>
                    )}
                  </Flex>
                  <Badge
                    size="1"
                    variant="soft"
                    color={item.releaseConversationId ? "blue" : "orange"}
                  >
                    {item.releaseConversationId ? "Session" : "Custom"}
                  </Badge>
                </Flex>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <Text size="2" color="gray">
                No items matching &quot;{searchQuery}&quot;
              </Text>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          <Text size="2" color="gray">
            No items in this test set
          </Text>
        </div>
      )}
    </div>
  );
}
